import { createWorker } from 'tesseract.js';

export const CARD_COMPANIES = [
  '국민카드',
  '신한카드',
  '현대카드',
  '삼성카드',
  '비씨카드',
  '롯데카드',
  '하나카드',
  '우리카드',
  '농협카드',
  '카카오뱅크',
  '토스뱅크',
  '기업은행',
  '직접 입력'
];

/**
 * Pre-processes receipt image on an offscreen canvas to maximize OCR contrast and text clarity
 */
async function preprocessImage(imageSource) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Scale down if image is ultra high resolution for faster processing while maintaining clarity
        const maxDimension = 1800;
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);

        // Enhance image: Grayscale & Contrast boost for thermal receipt papers
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        const contrast = 1.35; // 35% contrast boost
        const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));

        for (let i = 0; i < data.length; i += 4) {
          // Grayscale luminosity
          const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          // Contrast
          const enhanced = Math.min(255, Math.max(0, factor * (gray - 128) + 128));
          data[i] = enhanced;
          data[i + 1] = enhanced;
          data[i + 2] = enhanced;
        }

        ctx.putImageData(imageData, 0, 0);
        canvas.toBlob((blob) => {
          resolve(blob || imageSource);
        }, 'image/jpeg', 0.92);
      } catch (e) {
        console.warn('Canvas pre-processing skipped:', e);
        resolve(imageSource);
      }
    };

    img.onerror = () => resolve(imageSource);

    if (imageSource instanceof File || imageSource instanceof Blob) {
      img.src = URL.createObjectURL(imageSource);
    } else if (typeof imageSource === 'string') {
      img.src = imageSource;
    } else {
      resolve(imageSource);
    }
  });
}

/**
 * Scan receipt image using Tesseract OCR (kor + eng)
 * @param {File|Blob|string} imageSource - Image file, blob, or data URL
 * @param {Function} onProgress - Progress callback (percentage 0-100)
 * @returns {Promise<{rawText: string, amount: number|null, merchant: string, cardCompany: string, cardNumber: string, date: string}>}
 */
export async function scanReceiptOcr(imageSource, onProgress = () => {}) {
  let worker = null;
  try {
    const processedSource = await preprocessImage(imageSource);

    worker = await createWorker('kor+eng', 1, {
      logger: m => {
        if (m.status === 'recognizing text' && onProgress) {
          onProgress(Math.round((m.progress || 0) * 100));
        }
      }
    });

    const { data: { text } } = await worker.recognize(processedSource);
    await worker.terminate();

    return parseReceiptText(text);
  } catch (err) {
    if (worker) {
      try {
        await worker.terminate();
      } catch (_) {}
    }
    console.error('OCR recognition error:', err);
    throw err;
  }
}

/**
 * Intelligent parser to extract Amount, Merchant, Card Company, Card Number, and Date from OCR text
 */
export function parseReceiptText(rawText) {
  if (!rawText) {
    return { rawText: '', amount: null, merchant: '', cardCompany: '국민카드', cardNumber: '', date: '' };
  }

  const lines = rawText.split('\n').map(l => l.trim()).filter(Boolean);
  const fullText = rawText.replace(/\s+/g, ' ');
  
  let recognizedAmount = null;
  let recognizedMerchant = '';
  let recognizedCardCompany = '';
  let recognizedCardNumber = '';
  let recognizedDate = '';

  // 1. Detect Card Company (12 Korean Brands)
  const cardPatterns = [
    { company: '국민카드', regex: /(KB국민|국민카드|KB\s*Card|국민체크|국민신용|KB|국민)/i },
    { company: '신한카드', regex: /(신한카드|Shinhan|신한체크|신한신용|신한)/i },
    { company: '현대카드', regex: /(현대카드|Hyundai|현대체크|현대신용|현대)/i },
    { company: '삼성카드', regex: /(삼성카드|Samsung|삼성체크|삼성신용|삼성)/i },
    { company: '비씨카드', regex: /(비씨카드|BC카드|BC\s*Card|페이북|비씨|BC)/i },
    { company: '롯데카드', regex: /(롯데카드|Lotte|롯데체크|롯데신용|롯데)/i },
    { company: '하나카드', regex: /(하나카드|외환카드|Hana|하나체크|하나신용|하나SK|하나|외환)/i },
    { company: '우리카드', regex: /(우리카드|Woori|우리체크|우리신용|우리)/i },
    { company: '농협카드', regex: /(농협카드|NH농협|NH카드|채움카드|농협체크|농협신용|농협|NH)/i },
    { company: '카카오뱅크', regex: /(카카오뱅크|카카오카드|카카오체크|KakaoBank|카카오)/i },
    { company: '토스뱅크', regex: /(토스뱅크|토스카드|토스체크|TossBank|토스)/i },
    { company: '기업은행', regex: /(기업은행|IBK기업|IBK카드|기업체크|IBK)/i }
  ];

  for (const p of cardPatterns) {
    if (p.regex.test(fullText)) {
      recognizedCardCompany = p.company;
      break;
    }
  }
  if (!recognizedCardCompany) {
    recognizedCardCompany = '국민카드'; // default fallback
  }

  // 2. Detect Card Number (e.g. 9410-****-****-1234 or ****-****-****-5678)
  const cardNumberRegex = /([0-9*]{4}[-\s]+[0-9*]{2,4}[-\s]+[0-9*]{2,4}[-\s]+[0-9*]{3,4})/i;
  for (const line of lines) {
    const cardMatch = line.match(cardNumberRegex);
    if (cardMatch) {
      recognizedCardNumber = cardMatch[1].replace(/\s+/g, '-');
      break;
    }
  }

  // 3. Parse Total Amount
  const amountKeywords = /(합\s*계|총\s*액|결제금액|승인금액|받을금액|청구금액|결제액|카드결제|신용승인|Total|TOTAL|Amount|KRW|₩)/i;
  
  // Scan lines from bottom to top for total payment amount
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    if (amountKeywords.test(line)) {
      const match = line.match(/([0-9]{1,3}(?:,[0-9]{3})+|[0-9]{4,8})/);
      if (match) {
        const num = parseInt(match[0].replace(/,/g, ''), 10);
        if (num >= 100 && num < 100000000) {
          recognizedAmount = num;
          break;
        }
      }
    }
  }

  // Fallback: search any number with currency format
  if (!recognizedAmount) {
    const allMatches = [];
    lines.forEach(line => {
      const match = line.match(/([0-9]{1,3}(?:,[0-9]{3})+)\s*원?/g);
      if (match) {
        match.forEach(m => {
          const clean = parseInt(m.replace(/[^0-9]/g, ''), 10);
          if (clean >= 100 && clean < 100000000) allMatches.push(clean);
        });
      }
    });
    if (allMatches.length > 0) {
      recognizedAmount = Math.max(...allMatches);
    }
  }

  // 4. Parse Merchant Name (Usually labeled with 상호/가맹점 or top lines)
  const merchantExcludeKeywords = /(영수증|매출전표|신용카드|체크카드|카드승인|거래일시|고객용|가맹점번호|사업자번호|대표자?|TEL|전화|일시|주문번호|테이블|단말기|POS|부가세|공급가액|합계|결제|승인번호|취소)/i;
  const merchantPrefixRegex = /(상호|가맹점명|매장명|가맹점|상호명|사업장명|업소명)\s*[:：]?\s*(.+)/i;

  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    const line = lines[i];
    const prefixMatch = line.match(merchantPrefixRegex);
    if (prefixMatch && prefixMatch[2]) {
      const candidate = prefixMatch[2].replace(/[()]/g, '').trim();
      if (candidate.length >= 2) {
        recognizedMerchant = candidate;
        break;
      }
    }
  }

  if (!recognizedMerchant) {
    for (let i = 0; i < Math.min(lines.length, 6); i++) {
      const line = lines[i];
      if (!merchantExcludeKeywords.test(line) && line.length >= 2 && line.length <= 25 && !/^[0-9\s:.-]+$/.test(line)) {
        recognizedMerchant = line.replace(/[()]/g, '').trim();
        break;
      }
    }
  }

  // 5. Parse Date (YYYY-MM-DD, YYYY.MM.DD, YYYY/MM/DD, YYYY년 MM월 DD일, 승인일시: YYYYMMDD, YY-MM-DD, YY.MM.DD, etc.)
  const parseDateFromText = (text) => {
    if (!text) return null;
    
    // 1. Full 4-digit year with delimiters and optional spaces: 2026. 08. 19, 2026-8-19, 2026/08/19
    const match1 = text.match(/(20[1-3][0-9])\s*[.\-/]\s*([0-1]?[0-9])\s*[.\-/]\s*([0-3]?[0-9])/);
    if (match1) {
      const y = match1[1];
      const m = match1[2].padStart(2, '0');
      const d = match1[3].padStart(2, '0');
      if (parseInt(m, 10) >= 1 && parseInt(m, 10) <= 12 && parseInt(d, 10) >= 1 && parseInt(d, 10) <= 31) {
        return `${y}-${m}-${d}`;
      }
    }

    // 2. Korean date format: 2026년 8월 19일, 2026년08월19일
    const match2 = text.match(/(20[1-3][0-9])\s*년\s*([0-1]?[0-9])\s*월\s*([0-3]?[0-9])\s*일?/);
    if (match2) {
      const y = match2[1];
      const m = match2[2].padStart(2, '0');
      const d = match2[3].padStart(2, '0');
      if (parseInt(m, 10) >= 1 && parseInt(m, 10) <= 12 && parseInt(d, 10) >= 1 && parseInt(d, 10) <= 31) {
        return `${y}-${m}-${d}`;
      }
    }

    // 3. Compact 8-digit date with label: 승인일시: 20260819, 거래일자: 20260819
    const match3 = text.match(/(?:거래일시|승인일시|일시|판매일|결제일|승인일|매출일|일자|DATE)?\s*[:：]?\s*(20[1-3][0-9])(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])/i);
    if (match3) {
      const y = match3[1];
      const m = match3[2];
      const d = match3[3];
      return `${y}-${m}-${d}`;
    }

    // 4. 2-digit year: 26-08-19, 26. 8. 19, 26/08/19
    const match4 = text.match(/(?:^|[^\d])(2[0-9])\s*[.\-/]\s*([0-1]?[0-9])\s*[.\-/]\s*([0-3]?[0-9])/);
    if (match4) {
      const y = `20${match4[1]}`;
      const m = match4[2].padStart(2, '0');
      const d = match4[3].padStart(2, '0');
      if (parseInt(m, 10) >= 1 && parseInt(m, 10) <= 12 && parseInt(d, 10) >= 1 && parseInt(d, 10) <= 31) {
        return `${y}-${m}-${d}`;
      }
    }

    return null;
  };

  // Scan line by line first
  for (const line of lines) {
    const d = parseDateFromText(line);
    if (d) {
      recognizedDate = d;
      break;
    }
  }

  // Fallback: scan whole raw text
  if (!recognizedDate) {
    recognizedDate = parseDateFromText(rawText) || '';
  }

  return {
    rawText,
    amount: recognizedAmount,
    merchant: recognizedMerchant,
    cardCompany: recognizedCardCompany,
    cardNumber: recognizedCardNumber,
    date: recognizedDate
  };
}
